import React, { useMemo } from "react";
import { SnackbarProvider, useSnackbar } from "notistack";
import { makeStyles } from "@material-ui/core/styles";
import { brand } from "../StyledComponents/theme";

const useSnackbarStyles = makeStyles({
  variantSuccess: {
    backgroundColor: `${brand.green} !important`,
    fontFamily: "'Open Sans', sans-serif",
    borderRadius: "8px !important",
  },
  variantError: {
    fontFamily: "'Open Sans', sans-serif",
    borderRadius: "8px !important",
  },
});

const FIREBASE_MESSAGES = {
  "auth/invalid-email": "That email address doesn't look right.",
  "auth/user-not-found": "We couldn't find an account with that email.",
  "auth/wrong-password": "Incorrect email or password.",
  "auth/invalid-credential": "Incorrect email or password.",
  "auth/too-many-requests":
    "Too many attempts. Please wait a moment and try again.",
  "auth/email-already-in-use": "An account with that email already exists.",
  "auth/weak-password":
    "Please choose a stronger password (at least 8 characters).",
  "auth/requires-recent-login":
    "For security, please sign out and back in before making this change.",
  "auth/network-request-failed":
    "We couldn't reach the server. Check your connection and try again.",
  "permission-denied": "You don't have permission to do that.",
  "not-found": "We couldn't find what you were looking for.",
  "resource-exhausted": "Too many attempts. Please try again later.",
};

const GENERIC_MESSAGE = "Something went wrong. Please try again.";

/**
 * Turns a Firebase error (or any thrown value) into copy fit for a human.
 * Strings that look like stringified objects fall back to the generic
 * message rather than leaking internals.
 */
export const friendlyMessage = (err) => {
  if (!err) return GENERIC_MESSAGE;

  if (typeof err === "string") {
    return err.includes("{") ? GENERIC_MESSAGE : err;
  }

  const code = err.code && err.code.replace("functions/", "");
  if (code && FIREBASE_MESSAGES[code]) {
    return FIREBASE_MESSAGES[code];
  }

  return GENERIC_MESSAGE;
};

export const ErrorProvider = ({ children }) => {
  const classes = useSnackbarStyles();

  return (
    <SnackbarProvider
      maxSnack={3}
      classes={classes}
      anchorOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
    >
      {children}
    </SnackbarProvider>
  );
};

export const useError = () => {
  const { enqueueSnackbar } = useSnackbar();
  return useMemo(
    () => ({
      showError: (messageOrError) =>
        enqueueSnackbar(friendlyMessage(messageOrError), { variant: "error" }),
      showSuccess: (message) =>
        enqueueSnackbar(message, { variant: "success" }),
    }),
    [enqueueSnackbar]
  );
};
